/* Shared carousel behaviour on top of Dawn's slider-component, used by the
   PDP proof carousel and the PDP ingredients mobile slider — one dots
   implementation, not one per section.

   Pages: Dawn counts pages as slides minus whole slides per view, but a
   fractional per-view (proof shows 1.5) runs out of scroll before Dawn's
   last page. The reachable page count is recomputed from the actual scroll
   range and drives everything below; writing sc.totalPages keeps Dawn's own
   counter updates on the corrected value until its initPages recomputes,
   which the ResizeObserver re-corrects.

   Dots: one button per reachable page, current one marked with
   aria-current, clicking scrolls to that page. The last page's target is
   the end of the track, not a whole-slide multiple, so "go to the end"
   really reaches it. The liquid-rendered counter stays in the DOM,
   visually hidden, as the live region Dawn writes into. A track with no
   overflow (ingredients at desktop, where the cards are a grid) gets no
   dots at all.

   Autoplay (only when the section sets data-autoplay, seconds per card):
   advances by clicking Dawn's own next button, so paging and disabled
   states stay Dawn's. At the end it scrolls back to the start; no slide
   cloning. Pauses on hover and focus, and never runs under
   prefers-reduced-motion (including when the preference changes
   mid-visit). */

class NihonCarousel extends HTMLElement {
  connectedCallback() {
    this.sliderComponent = this.querySelector('slider-component');
    if (!this.sliderComponent) return;

    this.track = this.sliderComponent.querySelector('.slider');
    this.dotsContainer = this.sliderComponent.querySelector('.nihon-carousel__dots');
    if (this.track) {
      this.track.addEventListener('scroll', () => this.syncPages());
      new ResizeObserver(() => this.syncPages()).observe(this.track);
      this.syncPages();
    }

    const seconds = parseInt(this.dataset.autoplay || '0', 10);
    if (!seconds) return;

    this.intervalMs = seconds * 1000;
    this.paused = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.addEventListener('mouseenter', () => this.setPaused(true));
    this.addEventListener('mouseleave', () => this.setPaused(false));
    this.addEventListener('focusin', () => this.setPaused(true));
    this.addEventListener('focusout', () => this.setPaused(false));
    this.reducedMotion.addEventListener('change', () => this.sync());
    this.sync();
  }

  disconnectedCallback() {
    this.stop();
  }

  syncPages() {
    const sc = this.sliderComponent;
    if (!sc.sliderItemOffset) return;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const pages = maxScroll > 0 ? Math.round(maxScroll / sc.sliderItemOffset) + 1 : 1;
    this.stops = Array.from({ length: pages }, (_, i) => (i === pages - 1 ? maxScroll : i * sc.sliderItemOffset));
    sc.totalPages = pages;

    if (this.dotsContainer && this.dotsContainer.children.length !== pages) {
      this.dotsContainer.textContent = '';
      if (pages > 1) {
        const label = this.dotsContainer.dataset.dotLabel || 'Go to slide';
        for (let i = 0; i < pages; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'nihon-carousel__dot';
          dot.setAttribute('aria-label', `${label} ${i + 1}`);
          // No behavior argument: the scroll inherits the track's CSS
          // scroll-behavior, exactly as Dawn's own setSlidePosition does, so
          // dots and arrows animate identically — and the CSS smooth is
          // gated on prefers-reduced-motion, which an explicit
          // behavior: 'smooth' here would have ignored.
          dot.addEventListener('click', () => this.track.scrollTo({ left: this.stops[i] }));
          this.dotsContainer.appendChild(dot);
        }
      }
    }
    this.updateActive();
  }

  updateActive() {
    if (!this.stops) return;
    const x = this.track.scrollLeft;
    let active = 0;
    this.stops.forEach((stop, i) => {
      if (Math.abs(stop - x) < Math.abs(this.stops[active] - x)) active = i;
    });
    if (this.dotsContainer) {
      Array.from(this.dotsContainer.children).forEach((dot, i) => {
        if (i === active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }
    const sc = this.sliderComponent;
    if (sc.currentPageElement && sc.pageTotalElement) {
      sc.currentPageElement.textContent = active + 1;
      sc.pageTotalElement.textContent = this.stops.length;
    }
  }

  setPaused(paused) {
    this.paused = paused;
  }

  sync() {
    if (this.reducedMotion.matches) {
      this.stop();
    } else {
      this.start();
    }
  }

  start() {
    if (this.timer) return;
    this.timer = setInterval(() => this.advance(), this.intervalMs);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  advance() {
    if (this.paused || document.hidden) return;
    const next = this.sliderComponent.querySelector('button[name="next"]');
    if (!next || !this.track) return;
    if (next.hasAttribute('disabled')) {
      this.track.scrollTo({ left: 0 });
    } else {
      next.click();
    }
  }
}

customElements.define('nihon-carousel', NihonCarousel);
